import { useState, useEffect } from "react";
import { IconEdit, IconTrash, IconGripVertical, IconX, IconPlus, IconDownload } from "@tabler/icons-react";
import { getYearTypeName, Rocnik } from "../Admin";
import { validateRodCislo, autoFormatRodCislo } from "../../../utils/rodCisloValidation";
import { authenticatedFetch } from "../../../utils/auth";

interface Settings {
    id?: string;
    VPvalue?: number;
    CPvalue?: number;
    startDate?: string;
    CPdate?: string;
    finalDate?: string;
    zapisneValue?: number;
    baseKruzok?: number;
    baseStretko?: number;
    // Animátorov, Stredoškolákov a starších variants
    VPvalueSsAnim?: number;
    CPvalueSsAnim?: number;
    zapisneValueSsAnim?: number;
    baseKruzokSsAnim?: number;
    baseStretkoSsAnim?: number;
    bernKazdyDalsi?: number;

    principal?: string;
    updatedAt?: string;
}

interface IsAnim {
    id: string;
    rodCislo: string;
    name: string;
}

interface MunicipalityOfficeAddress {
    id: string;
    name: string;
    type: string;
    street: string;
    city: string;
    psc: string;
}

const Vseobecne = ({ rocnik, setRocnik }: { rocnik: Rocnik[], setRocnik: React.Dispatch<React.SetStateAction<Rocnik[]>> }) => {
    const [rocnikPopUp, setRocnikPopup] = useState<boolean>(false);
    const [editingRocnik, setEditingRocnik] = useState<Rocnik | null>(null);
    const [draggedItem, setDraggedItem] = useState<Rocnik | null>(null);
    const [sortedRocnik, setSortedRocnik] = useState<Rocnik[]>([]);

    // Settings state
    const [settings, setSettings] = useState<Settings | null>(null);
    const [settingsLoading, setSettingsLoading] = useState<boolean>(false);

    // IsAnim state
    const [isAnimList, setIsAnimList] = useState<IsAnim[]>([]);
    const [isAnimPopUp, setIsAnimPopup] = useState<boolean>(false);
    const [editingIsAnim, setEditingIsAnim] = useState<IsAnim | null>(null);
    const [isAnimLoading, setIsAnimLoading] = useState<boolean>(false);
    const [newIsAnimRodCislo, setNewIsAnimRodCislo] = useState<string>('');

    // Import state
    const [importPopUp, setImportPopup] = useState<boolean>(false);
    const [importLoading, setImportLoading] = useState<boolean>(false);
    const [importResult, setImportResult] = useState<any>(null);

    const [municipalityAddresses, setMunicipalityAddresses] = useState<MunicipalityOfficeAddress[]>([]);
    const [municipalityAddressesLoading, setMunicipalityAddressesLoading] = useState<boolean>(false);
    const [addressPopup, setAddressPopup] = useState<boolean>(false);
    const [editingAddress, setEditingAddress] = useState<MunicipalityOfficeAddress | null>(null);
    const [addressImportPopup, setAddressImportPopup] = useState<boolean>(false);
    const [addressImportLoading, setAddressImportLoading] = useState<boolean>(false);

    // Update sorted rocnik when rocnik prop changes
    useEffect(() => {
        setSortedRocnik([...rocnik].sort((a, b) => a.order - b.order));
    }, [rocnik]);
    const onSubmitRocnik = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const name = formData.get("name") as string;
        const eduName = formData.get("eduName") as string;
        const type = formData.get("type") as string;
        if (!name || !type) {
            alert("Všetky povinné polia musia byť vyplnené!");
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ name, eduName: eduName || null, type }),
            });

            if (!response.ok) {
                throw new Error("Failed to add rocnik");
            }

            const data = await response.json();

            setRocnik([...rocnik, data.data]);
            setRocnikPopup(false);
            (e.target as HTMLFormElement).reset();
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error adding rocnik:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error adding rocnik:", error);
            }
        }
    };

    const onSubmitEditRocnik = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!editingRocnik) return;

        const formData = new FormData(e.currentTarget);
        const name = formData.get("name") as string;
        const eduName = formData.get("eduName") as string;
        const type = formData.get("type") as string;
        if (!name || !type) {
            alert("Všetky povinné polia musia byť vyplnené!");
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik/${editingRocnik.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ name, eduName: eduName || null, type }),
            });

            if (!response.ok) {
                throw new Error("Failed to update rocnik");
            }

            const data = await response.json();
            const updatedRocnik = rocnik.map(r => r.id === editingRocnik.id ? data.data : r);
            setRocnik(updatedRocnik);
            setEditingRocnik(null);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error updating rocnik:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error updating rocnik:", error);
            }
        }
    };

    const deleteRocnik = async (id: string) => {
        if (!window.confirm("Ste si istí, že chcete vymazať tento ročník?")) {
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Failed to delete rocnik");
            }

            const updatedRocnik = rocnik.filter(r => r.id !== id);
            setRocnik(updatedRocnik);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error deleting rocnik:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error deleting rocnik:", error);
            }
        }
    };

    const handleDragStart = (e: React.DragEvent, item: Rocnik) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = async (e: React.DragEvent, targetItem: Rocnik) => {
        e.preventDefault();
        if (!draggedItem || draggedItem.id === targetItem.id) {
            setDraggedItem(null);
            return;
        }

        const newSortedRocnik = [...sortedRocnik];
        const draggedIndex = newSortedRocnik.findIndex(item => item.id === draggedItem.id);
        const targetIndex = newSortedRocnik.findIndex(item => item.id === targetItem.id);

        // Remove dragged item and insert at target position
        newSortedRocnik.splice(draggedIndex, 1);
        newSortedRocnik.splice(targetIndex, 0, draggedItem);

        // Update order values
        const reorderedRocnik = newSortedRocnik.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/rocnik/reorder`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    rocniky: reorderedRocnik.map(r => ({ id: r.id, order: r.order }))
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to reorder rocnik");
            }

            setSortedRocnik(reorderedRocnik);
            setRocnik(reorderedRocnik);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error reordering rocnik:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error reordering rocnik:", error);
            }
        }

        setDraggedItem(null);
    };

    // Settings functions
    const fetchSettings = async () => {
        setSettingsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_PATH}/api/vseob/settings`);
            if (!response.ok) {
                throw new Error("Failed to fetch settings");
            }
            const data = await response.json();
            setSettings(data.data);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Error fetching settings:", error);
            }
            else {
                console.error("Error fetching settings:", error);
            }
        } finally {
            setSettingsLoading(false);
        }
    };

    const onSubmitSettings = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const VPvalue = formData.get("VPvalue") ? parseFloat(formData.get("VPvalue") as string) : undefined;
        const CPvalue = formData.get("CPvalue") ? parseFloat(formData.get("CPvalue") as string) : undefined;
        const zapisneValue = formData.get("zapisneValue") ? parseFloat(formData.get("zapisneValue") as string) : undefined;
        const baseKruzok = formData.get("baseKruzok") ? parseFloat(formData.get("baseKruzok") as string) : undefined;
        const baseStretko = formData.get("baseStretko") ? parseFloat(formData.get("baseStretko") as string) : undefined;

        // Animátorov, Stredoškolákov a starších variants
        const VPvalueSsAnim = formData.get("VPvalueSsAnim") ? parseFloat(formData.get("VPvalueSsAnim") as string) : undefined;
        const CPvalueSsAnim = formData.get("CPvalueSsAnim") ? parseFloat(formData.get("CPvalueSsAnim") as string) : undefined;
        const zapisneValueSsAnim = formData.get("zapisneValueSsAnim") ? parseFloat(formData.get("zapisneValueSsAnim") as string) : undefined;
        const baseKruzokSsAnim = formData.get("baseKruzokSsAnim") ? parseFloat(formData.get("baseKruzokSsAnim") as string) : undefined;
        const baseStretkoSsAnim = formData.get("baseStretkoSsAnim") ? parseFloat(formData.get("baseStretkoSsAnim") as string) : undefined;
        const bernKazdyDalsi = formData.get("bernKazdyDalsi") ? parseFloat(formData.get("bernKazdyDalsi") as string) : undefined;

        const CPdate = formData.get("CPdate") as string || undefined;
        const startDate = formData.get("startDate") as string || undefined;
        const finalDate = formData.get("finalDate") as string || undefined;
        const principal = formData.get("principal") as string || undefined;

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    VPvalue,
                    CPvalue,
                    startDate,
                    zapisneValue,
                    baseKruzok,
                    baseStretko,
                    VPvalueSsAnim,
                    CPvalueSsAnim,
                    zapisneValueSsAnim,
                    baseKruzokSsAnim,
                    baseStretkoSsAnim,
                    bernKazdyDalsi,
                    CPdate,
                    finalDate,
                    principal
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to save settings");
            }

            const data = await response.json();
            setSettings(data.data);
            alert("Nastavenia boli úspešne uložené!");
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error saving settings:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error saving settings:", error);
            }
        }
    };

    // Load settings on component mount
    useEffect(() => {
        fetchSettings();
        fetchIsAnimList();
        fetchMunicipalityAddresses();
    }, []);

    // IsAnim functions
    const fetchIsAnimList = async () => {
        setIsAnimLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim`);
            if (!response.ok) {
                throw new Error("Failed to fetch IsAnim list");
            }
            const data = await response.json();
            setIsAnimList(data.data || []);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Error fetching IsAnim list:", error);
            }
            else {
                console.error("Error fetching IsAnim list:", error);
            }
        } finally {
            setIsAnimLoading(false);
        }
    };

    const onSubmitIsAnim = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const rodCislo = newIsAnimRodCislo; // Use state value
        const name = formData.get("name") as string;

        if (!rodCislo || !name) {
            alert("Všetky polia sú povinné!");
            return;
        }

        // Validate rodCislo format
        const validation = validateRodCislo(rodCislo);
        if (!validation.isValid) {
            alert(validation.error);
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ rodCislo, name }),
            });

            if (!response.ok) {
                throw new Error("Failed to add IsAnim");
            }

            const data = await response.json();
            setIsAnimList([...isAnimList, data.data]);
            setIsAnimPopup(false);
            setNewIsAnimRodCislo(''); // Reset state
            (e.target as HTMLFormElement).reset();
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error adding IsAnim:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error adding IsAnim:", error);
            }
        }
    };

    const onSubmitEditIsAnim = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!editingIsAnim) return;

        const formData = new FormData(e.currentTarget);
        const rodCislo = formData.get("rodCislo") as string;
        const name = formData.get("name") as string;

        if (!rodCislo || !name) {
            alert("Všetky polia sú povinné!");
            return;
        }

        // Validate rodCislo format
        const validation = validateRodCislo(rodCislo);
        if (!validation.isValid) {
            alert(validation.error);
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim/${editingIsAnim.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ rodCislo, name }),
            });

            if (!response.ok) {
                throw new Error("Failed to update IsAnim");
            }

            const data = await response.json();
            const updatedIsAnimList = isAnimList.map(item => item.id === editingIsAnim.id ? data.data : item);
            setIsAnimList(updatedIsAnimList);
            setEditingIsAnim(null);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error updating IsAnim:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error updating IsAnim:", error);
            }
        }
    };

    const deleteIsAnim = async (id: string) => {
        if (!window.confirm("Ste si istí, že chcete vymazať tento záznam?")) {
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Failed to delete IsAnim");
            }

            const updatedIsAnimList = isAnimList.filter(item => item.id !== id);
            setIsAnimList(updatedIsAnimList);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error deleting IsAnim:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error deleting IsAnim:", error);
            }
        }
    };

    const onSubmitImport = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setImportLoading(true);
        setImportResult(null);

        const formData = new FormData(e.currentTarget);
        const excelFile = formData.get("excelFile") as File;

        if (!excelFile) {
            alert("Prosím vyberte Excel súbor!");
            setImportLoading(false);
            return;
        }

        try {
            const uploadFormData = new FormData();
            uploadFormData.append('excelFile', excelFile);

            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim/import`, {
                method: "POST",
                body: uploadFormData,
            });

            if (!response.ok) {
                throw new Error("Failed to import Excel file");
            }

            const data = await response.json();
            setImportResult(data);

            // Refresh the IsAnim list
            fetchIsAnimList();

            alert(`Import dokončený! Pridané: ${data.summary.added}, Preskočené: ${data.summary.skipped}, Chyby: ${data.summary.errors}`);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error importing Excel:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error importing Excel:", error);
            }
        } finally {
            setImportLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim/export`);

            if (!response.ok) {
                throw new Error("Failed to export Excel file");
            }

            // Get the filename from response headers or generate one
            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'animatori_ss_export.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Convert response to blob
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error exporting Excel:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error exporting Excel:", error);
            }
        }
    };

    const handleDeleteAll = async () => {
        // Double confirmation for safety
        const firstConfirm = window.confirm(
            "POZOR! Toto vymaže VŠETKY záznamy animátorov a stredoškolákov. Táto akcia sa nedá vrátiť späť!\n\nSte si absolútne istí, že chcete pokračovať?"
        );

        if (!firstConfirm) {
            return;
        }

        const secondConfirm = window.confirm(
            "Posledné potvrdenie: Skutočne chcete vymazať VŠETKY záznamy?\n\nPo potvrdení sa všetky dáta natrvalo stratia!"
        );

        if (!secondConfirm) {
            return;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/isanim/all`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Failed to delete all records");
            }

            const data = await response.json();
            setIsAnimList([]);
            alert(data.message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                alert(error.message);
                console.error("Error deleting all records:", error);
            }
            else {
                alert("An unknown error occurred");
                console.error("Error deleting all records:", error);
            }
        }
    };

    const fetchMunicipalityAddresses = async () => {
        setMunicipalityAddressesLoading(true);
        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses`);
            if (!response.ok) {
                throw new Error("Nepodarilo sa načítať adresy obecných úradov.");
            }
            const data = await response.json();
            setMunicipalityAddresses(data.data);
        } catch (error: unknown) {
            console.error("Error fetching municipality office addresses:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        } finally {
            setMunicipalityAddressesLoading(false);
        }
    };

    const onSubmitAddress = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const formData = new FormData(form);
        const address = {
            name: formData.get("name") as string,
            type: formData.get("type") as string,
            street: formData.get("street") as string,
            city: formData.get("city") as string,
            psc: formData.get("psc") as string
        };

        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses${editingAddress ? `/${editingAddress.id}` : ""}`,
                {
                    method: editingAddress ? "PUT" : "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(address)
                }
            );
            if (!response.ok) {
                throw new Error(editingAddress ? "Nepodarilo sa upraviť adresu." : "Nepodarilo sa pridať adresu.");
            }
            const data = await response.json();
            if (editingAddress) {
                setMunicipalityAddresses(municipalityAddresses.map(item => item.id === editingAddress.id ? data.data : item));
            } else {
                setMunicipalityAddresses([...municipalityAddresses, data.data]);
            }
            setAddressPopup(false);
            setEditingAddress(null);
            form.reset();
        } catch (error: unknown) {
            console.error("Error saving municipality office address:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        }
    };

    const deleteMunicipalityAddress = async (id: string) => {
        if (!window.confirm("Ste si istí, že chcete vymazať túto adresu?")) return;
        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses/${id}`, {
                method: "DELETE"
            });
            if (!response.ok) {
                throw new Error("Nepodarilo sa vymazať adresu.");
            }
            setMunicipalityAddresses(municipalityAddresses.filter(item => item.id !== id));
        } catch (error: unknown) {
            console.error("Error deleting municipality office address:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        }
    };

    const deleteAllMunicipalityAddresses = async () => {
        const confirmed = window.confirm(
            "POZOR! Táto akcia vymaže všetky adresy obecných úradov a nedá sa vrátiť späť.\n\nNaozaj chcete pokračovať?"
        );
        if (!confirmed) return;

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses/all`, {
                method: "DELETE"
            });
            if (!response.ok) {
                throw new Error("Nepodarilo sa vymazať všetky adresy.");
            }
            const data = await response.json();
            setMunicipalityAddresses([]);
            alert(data.message);
        } catch (error: unknown) {
            console.error("Error deleting all municipality office addresses:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        }
    };

    const onSubmitAddressImport = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const excelFile = formData.get("addressExcelFile") as File;
        if (!excelFile) {
            alert("Prosím vyberte Excel súbor!");
            return;
        }

        setAddressImportLoading(true);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append("excelFile", excelFile);
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses/import`, {
                method: "POST",
                body: uploadFormData
            });
            if (!response.ok) {
                throw new Error("Nepodarilo sa importovať Excel súbor.");
            }
            const data = await response.json();
            await fetchMunicipalityAddresses();
            const rowErrors = data.errors?.slice(0, 3).join("\n") || "";
            alert(`Import dokončený! Pridané: ${data.summary.added}, chyby: ${data.summary.errors}${rowErrors ? `\n${rowErrors}` : ""}`);
            setAddressImportPopup(false);
        } catch (error: unknown) {
            console.error("Error importing municipality office addresses:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        } finally {
            setAddressImportLoading(false);
        }
    };

    const exportMunicipalityAddresses = async () => {
        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_PATH}/api/vseob/municipality-addresses/export`);
            if (!response.ok) {
                throw new Error("Nepodarilo sa exportovať Excel súbor.");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "adresy_obecnych_uradov.xlsx";
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error: unknown) {
            console.error("Error exporting municipality office addresses:", error);
            alert(error instanceof Error ? error.message : "Nastala neznáma chyba.");
        }
    };

    return (
        <>
            {/* Settings Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Nastavenia systému</h3>
                    <p className="text-sm text-gray-600 mt-1">Spravujte základné nastavenia aplikácie</p>
                </div>
                <div className="p-6">
                    <form onSubmit={onSubmitSettings} className="space-y-8">
                        {/* Standard Values Section */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Štandardné hodnoty</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="VPvalue" className="block text-sm font-medium text-gray-700 mb-1">
                                        Hodnota vzdelávacieho poukazu (€)
                                    </label>
                                    <input
                                        id="VPvalue"
                                        name="VPvalue"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.VPvalue || ''}
                                        placeholder="Zadajte hodnotu VP"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="CPvalue" className="block text-sm font-medium text-gray-700 mb-1">
                                        Hodnota čestného prehlásenia (€)
                                    </label>
                                    <input
                                        id="CPvalue"
                                        name="CPvalue"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.CPvalue || ''}
                                        placeholder="Zadajte hodnotu CP"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="zapisneValue" className="block text-sm font-medium text-gray-700 mb-1">
                                        Zápisné (€)
                                    </label>
                                    <input
                                        id="zapisneValue"
                                        name="zapisneValue"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.zapisneValue || ''}
                                        placeholder="Zadajte zápisné"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="baseKruzok" className="block text-sm font-medium text-gray-700 mb-1">
                                        Základný poplatok krúžok (€)
                                    </label>
                                    <input
                                        id="baseKruzok"
                                        name="baseKruzok"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.baseKruzok !== undefined ? settings.baseKruzok : ''}
                                        placeholder="Zadajte základný poplatok"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="baseStretko" className="block text-sm font-medium text-gray-700 mb-1">
                                        Základný poplatok stretko (€)
                                    </label>
                                    <input
                                        id="baseStretko"
                                        name="baseStretko"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.baseStretko !== undefined ? settings.baseStretko : ''}
                                        placeholder="Zadajte základný poplatok stretko"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="bernKazdyDalsi" className="block text-sm font-medium text-gray-700 mb-1">
                                        Príplatok za ďalší krúžok na Bernolákovej (€)
                                    </label>
                                    <input
                                        id="bernKazdyDalsi"
                                        name="bernKazdyDalsi"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.bernKazdyDalsi !== undefined ? settings.bernKazdyDalsi : ''}
                                        placeholder="Zadajte príplatok za ďalší krúžok"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Animátorov, Stredoškolákov a starších Values Section */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Hodnoty pre Animátorov, Stredoškolákov a starších</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="VPvalueSsAnim" className="block text-sm font-medium text-gray-700 mb-1">
                                        Hodnota vzdelávacieho poukazu (€)
                                    </label>
                                    <input
                                        id="VPvalueSsAnim"
                                        name="VPvalueSsAnim"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.VPvalueSsAnim || ''}
                                        placeholder="Zadajte hodnotu VP pre Animátorov, Stredoškolákov a starších"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="CPvalueSsAnim" className="block text-sm font-medium text-gray-700 mb-1">
                                        Hodnota čestného prehlásenia (€)
                                    </label>
                                    <input
                                        id="CPvalueSsAnim"
                                        name="CPvalueSsAnim"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.CPvalueSsAnim || ''}
                                        placeholder="Zadajte hodnotu CP pre Animátorov, Stredoškolákov a starších"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="zapisneValueSsAnim" className="block text-sm font-medium text-gray-700 mb-1">
                                        Zápisné (€)
                                    </label>
                                    <input
                                        id="zapisneValueSsAnim"
                                        name="zapisneValueSsAnim"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.zapisneValueSsAnim || ''}
                                        placeholder="Zadajte zápisné pre Animátorov, Stredoškolákov a starších"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="baseKruzokSsAnim" className="block text-sm font-medium text-gray-700 mb-1">
                                        Základný poplatok krúžok (€)
                                    </label>
                                    <input
                                        id="baseKruzokSsAnim"
                                        name="baseKruzokSsAnim"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.baseKruzokSsAnim !== undefined ? settings.baseKruzokSsAnim : ''}
                                        placeholder="Zadajte základný poplatok pre Animátorov, Stredoškolákov a starších"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="baseStretkoSsAnim" className="block text-sm font-medium text-gray-700 mb-1">
                                        Základný poplatok stretko (€)
                                    </label>
                                    <input
                                        id="baseStretkoSsAnim"
                                        name="baseStretkoSsAnim"
                                        type="number"
                                        step="0.01"
                                        defaultValue={settings?.baseStretkoSsAnim !== undefined ? settings.baseStretkoSsAnim : ''}
                                        placeholder="Zadajte základný poplatok stretko pre Animátorov, Stredoškolákov a starších"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Other Settings Section */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Ostatné nastavenia</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Dátum začiatku (startDate)
                                    </label>
                                    <input
                                        id="startDate"
                                        name="startDate"
                                        type="date"
                                        defaultValue={settings?.startDate ? new Date(settings.startDate).toISOString().split('T')[0] : ''}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="CPdate" className="block text-sm font-medium text-gray-700 mb-1">
                                        CP dátum
                                    </label>
                                    <input
                                        id="CPdate"
                                        name="CPdate"
                                        type="date"
                                        defaultValue={settings?.CPdate ? new Date(settings.CPdate).toISOString().split('T')[0] : ''}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>



                                <div>
                                    <label htmlFor="finalDate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Finálny dátum
                                    </label>
                                    <input
                                        id="finalDate"
                                        name="finalDate"
                                        type="date"
                                        defaultValue={settings?.finalDate ? new Date(settings.finalDate).toISOString().split('T')[0] : ''}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="principal" className="block text-sm font-medium text-gray-700 mb-1">
                                        Riaditeľ CVČ
                                    </label>
                                    <input
                                        id="principal"
                                        name="principal"
                                        type="text"
                                        defaultValue={settings?.principal || ''}
                                        placeholder="Zadajte meno riaditeľa"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>

                                <div></div>

                                <div className="flex items-end">
                                    <button
                                        type="submit"
                                        className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                        disabled={settingsLoading}
                                    >
                                        {settingsLoading ? 'Ukladanie...' : 'Uložiť nastavenia'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {sortedRocnik.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-lg font-semibold text-gray-900">Ročníky</h3>
                        <p className="text-sm text-gray-600 mt-1">Spravujte ročníky a ich poradie</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                                        <IconGripVertical size={16} className="text-gray-400" />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Názov</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edupage názov</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedRocnik.map((e: Rocnik, index: number) => (
                                    <tr
                                        key={e.id || index}
                                        draggable
                                        onDragStart={(event) => handleDragStart(event, e)}
                                        onDragOver={handleDragOver}
                                        onDrop={(event) => handleDrop(event, e)}
                                        className={`hover:bg-gray-50 transition-colors duration-150 cursor-move ${draggedItem?.id === e.id ? 'opacity-50' : ''
                                            }`}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <IconGripVertical size={16} className="text-gray-400 cursor-move" />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{e.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600">{e.eduName || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium  ${e.type === 'PRIMARY' ? 'bg-blue-100 text-blue-800' : (e.type === 'HIGHSCHOOL' ? 'bg-green-100 text-green-800' : (e.type === 'UNIVERSITY' ? 'bg-purple-100 text-purple-800' : 'bg-yellow-100 text-yellow-800'))}`}>
                                                {getYearTypeName(e.type)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => setEditingRocnik(e)}
                                                    className="inline-flex items-center p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                                                    title="Upraviť"
                                                >
                                                    <IconEdit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteRocnik(e.id)}
                                                    className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150"
                                                    title="Vymazať"
                                                >
                                                    <IconTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="mt-8">
                <button
                    onClick={() => setRocnikPopup(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                >
                    <IconPlus size={16} className="mr-2" />
                    Pridať Ročník
                </button>
            </div>

            {/* IsAnim Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Prípravkari a Animátori</h3>
                    <p className="text-sm text-gray-600 mt-1">Spravujte zoznam osôb oprávnených na zľavu</p>
                </div>
                <div className="p-6">
                    {isAnimLoading ? (
                        <div className="text-center py-4">
                            <div className="text-sm text-gray-500">Načítavam...</div>
                        </div>
                    ) : isAnimList.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meno</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rodné číslo</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {isAnimList.map((item: IsAnim) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-600">{item.rodCislo}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => setEditingIsAnim(item)}
                                                        className="inline-flex items-center p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                                                        title="Upraviť"
                                                    >
                                                        <IconEdit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteIsAnim(item.id)}
                                                        className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150"
                                                        title="Vymazać"
                                                    >
                                                        <IconTrash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-sm text-gray-500">Žiadne záznamy</div>
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={() => setIsAnimPopup(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                        >
                            <IconPlus size={16} className="mr-2" />
                            Pridať osobu
                        </button>
                        <button
                            onClick={() => setImportPopup(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                        >
                            <IconPlus size={16} className="mr-2" />
                            Import z Excelu
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-150"
                        >
                            <IconDownload size={16} className="mr-2" />
                            Export do Excelu
                        </button>
                        {isAnimList.length > 0 && (
                            <button
                                onClick={handleDeleteAll}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                            >
                                <IconTrash size={16} className="mr-2" />
                                Vymazať všetko
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Adresy obecných úradov</h3>
                    <p className="text-sm text-gray-600 mt-1">Spravujte adresy obecných úradov na budúci export</p>
                </div>
                <div className="p-6">
                    {municipalityAddressesLoading ? (
                        <div className="text-center py-4">
                            <div className="text-sm text-gray-500">Načítavam...</div>
                        </div>
                    ) : municipalityAddresses.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        {["Nazov", "Typ", "Ulica", "Obec", "PSC"].map(header => (
                                            <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{header}</th>
                                        ))}
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {municipalityAddresses.map(address => (
                                        <tr key={address.id} className="hover:bg-gray-50 transition-colors duration-150">
                                            <td className="px-4 py-4 text-sm font-medium text-gray-900">{address.name}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{address.type}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{address.street}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{address.city}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{address.psc}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingAddress(address);
                                                            setAddressPopup(true);
                                                        }}
                                                        className="inline-flex items-center p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                                                        title="Upraviť"
                                                    >
                                                        <IconEdit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteMunicipalityAddress(address.id)}
                                                        className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-150"
                                                        title="Vymazať"
                                                    >
                                                        <IconTrash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-sm text-gray-500">Žiadne záznamy</div>
                        </div>
                    )}
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={() => {
                                setEditingAddress(null);
                                setAddressPopup(true);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                        >
                            <IconPlus size={16} className="mr-2" />
                            Pridať adresu
                        </button>
                        <button
                            onClick={() => setAddressImportPopup(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <IconPlus size={16} className="mr-2" />
                            Import z Excelu
                        </button>
                        <button
                            onClick={exportMunicipalityAddresses}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
                        >
                            <IconDownload size={16} className="mr-2" />
                            Export do Excelu
                        </button>
                        {municipalityAddresses.length > 0 && (
                            <button
                                onClick={deleteAllMunicipalityAddresses}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                            >
                                <IconTrash size={16} className="mr-2" />
                                Vymazať všetko
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {rocnikPopUp &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Pridať ročník</h2>
                                <button
                                    onClick={() => setRocnikPopup(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitRocnik} className="space-y-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Názov
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        placeholder="Názov ročníka"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="eduName" className="block text-sm font-medium text-gray-700 mb-1">
                                        Edupage názov
                                    </label>
                                    <input
                                        id="eduName"
                                        name="eduName"
                                        type="text"
                                        placeholder="Edupage názov ročníka"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                                        Typ
                                    </label>
                                    <select
                                        id="type"
                                        name="type"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="PRESCHOOL">{getYearTypeName('PRESCHOOL')}</option>
                                        <option value="PRIMARY">{getYearTypeName('PRIMARY')}</option>
                                        <option value="HIGHSCHOOL">{getYearTypeName('HIGHSCHOOL')}</option>
                                        <option value="UNIVERSITY">{getYearTypeName('UNIVERSITY')}</option>
                                    </select>
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setRocnikPopup(false)}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        Pridať
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {editingRocnik &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Upraviť ročník</h2>
                                <button
                                    onClick={() => setEditingRocnik(null)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitEditRocnik} className="space-y-4">
                                <div>
                                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Názov
                                    </label>
                                    <input
                                        id="edit-name"
                                        name="name"
                                        type="text"
                                        defaultValue={editingRocnik.name}
                                        placeholder="Názov ročníka"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="edit-eduName" className="block text-sm font-medium text-gray-700 mb-1">
                                        Edupage názov
                                    </label>
                                    <input
                                        id="edit-eduName"
                                        name="eduName"
                                        type="text"
                                        defaultValue={editingRocnik.eduName || ''}
                                        placeholder="Edupage názov ročníka"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700 mb-1">
                                        Typ
                                    </label>
                                    <select
                                        id="edit-type"
                                        name="type"
                                        defaultValue={editingRocnik.type}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="PRESCHOOL">{getYearTypeName('PRESCHOOL')}</option>
                                        <option value="PRIMARY">{getYearTypeName('PRIMARY')}</option>
                                        <option value="HIGHSCHOOL">{getYearTypeName('HIGHSCHOOL')}</option>
                                        <option value="UNIVERSITY">{getYearTypeName('UNIVERSITY')}</option>

                                    </select>
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setEditingRocnik(null)}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        Uložiť
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {/* IsAnim Add Modal */}
            {isAnimPopUp &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Pridať osobu</h2>
                                <button
                                    onClick={() => setIsAnimPopup(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitIsAnim} className="space-y-4">
                                <div>
                                    <label htmlFor="isanim-name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Meno a priezvisko
                                    </label>
                                    <input
                                        id="isanim-name"
                                        name="name"
                                        type="text"
                                        placeholder="Zadajte meno a priezvisko"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="isanim-rodcislo" className="block text-sm font-medium text-gray-700 mb-1">
                                        Rodné číslo
                                    </label>
                                    <input
                                        id="isanim-rodcislo"
                                        name="rodCislo"
                                        type="text"
                                        value={newIsAnimRodCislo}
                                        onChange={(e) => {
                                            const formattedValue = autoFormatRodCislo(e.target.value);
                                            setNewIsAnimRodCislo(formattedValue);
                                        }}
                                        placeholder="XXXXXX/XXXX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsAnimPopup(false)}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                    >
                                        Pridať
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {/* IsAnim Edit Modal */}
            {editingIsAnim &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Upraviť osobu</h2>
                                <button
                                    onClick={() => setEditingIsAnim(null)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitEditIsAnim} className="space-y-4">
                                <div>
                                    <label htmlFor="edit-isanim-name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Meno a priezvisko
                                    </label>
                                    <input
                                        id="edit-isanim-name"
                                        name="name"
                                        type="text"
                                        defaultValue={editingIsAnim.name}
                                        placeholder="Zadajte meno a priezvisko"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="edit-isanim-rodcislo" className="block text-sm font-medium text-gray-700 mb-1">
                                        Rodné číslo
                                    </label>
                                    <input
                                        id="edit-isanim-rodcislo"
                                        name="rodCislo"
                                        type="text"
                                        defaultValue={editingIsAnim.rodCislo}
                                        onChange={(e) => {
                                            const formattedValue = autoFormatRodCislo(e.target.value);
                                            e.target.value = formattedValue;
                                        }}
                                        placeholder="XXXXXX/XXXX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        required
                                    />
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setEditingIsAnim(null)}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                    >
                                        Uložiť
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {/* Import Modal */}
            {importPopUp &&
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Import z Excel súboru</h2>
                                <button
                                    onClick={() => setImportPopup(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>

                            <div className="mb-4 p-4 bg-blue-50 rounded-md">
                                <h4 className="text-sm font-medium text-blue-800 mb-2">Formát súboru:</h4>
                                <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                                    <li>Excel súbor (.xlsx alebo .xls)</li>
                                    <li>Prvý riadok: hlavička (Meno | Rodné číslo)</li>
                                    <li>Formát rodného čísla: 123456/1234</li>
                                    <li>Duplikáty budú preskočené</li>
                                </ul>
                            </div>

                            <form onSubmit={onSubmitImport} className="space-y-4">
                                <div>
                                    <label htmlFor="excelFile" className="block text-sm font-medium text-gray-700 mb-1">
                                        Vyberte Excel súbor
                                    </label>
                                    <input
                                        id="excelFile"
                                        name="excelFile"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                {importResult && (
                                    <div className="mt-4 p-4 bg-green-50 rounded-md">
                                        <h4 className="text-sm font-medium text-green-800 mb-2">Výsledok importu:</h4>
                                        <div className="text-sm text-green-700">
                                            <p>Celkom riadkov: {importResult.summary.total}</p>
                                            <p>Pridané: {importResult.summary.added}</p>
                                            <p>Preskočené (duplikáty): {importResult.summary.skipped}</p>
                                            <p>Chyby: {importResult.summary.errors}</p>
                                        </div>
                                        {importResult.errors && importResult.errors.length > 0 && (
                                            <div className="mt-2">
                                                <h5 className="text-sm font-medium text-red-800">Chyby:</h5>
                                                <ul className="text-sm text-red-700 list-disc list-inside mt-1">
                                                    {importResult.errors.slice(0, 5).map((error: string, index: number) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                    {importResult.errors.length > 5 && (
                                                        <li>... a ďalších {importResult.errors.length - 5} chýb</li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setImportPopup(false);
                                            setImportResult(null);
                                        }}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                    >
                                        {importResult ? 'Zavrieť' : 'Zrušiť'}
                                    </button>
                                    {!importResult && (
                                        <button
                                            type="submit"
                                            disabled={importLoading}
                                            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150 disabled:opacity-50"
                                        >
                                            {importLoading ? 'Importujem...' : 'Importovať'}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            }

            {addressPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {editingAddress ? "Upraviť adresu obecného úradu" : "Pridať adresu obecného úradu"}
                                </h2>
                                <button
                                    onClick={() => {
                                        setAddressPopup(false);
                                        setEditingAddress(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <form onSubmit={onSubmitAddress} className="space-y-4">
                                {([
                                    ["name", "Nazov"],
                                    ["type", "Typ"],
                                    ["street", "Ulica"],
                                    ["city", "Obec"],
                                    ["psc", "PSC"]
                                ] as const).map(([field, label]) => (
                                    <div key={field}>
                                        <label htmlFor={`address-${field}`} className="block text-sm font-medium text-gray-700 mb-1">
                                            {label}
                                        </label>
                                        <input
                                            id={`address-${field}`}
                                            name={field}
                                            type="text"
                                            defaultValue={editingAddress?.[field] || ""}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                            required
                                        />
                                    </div>
                                ))}
                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAddressPopup(false);
                                            setEditingAddress(null);
                                        }}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
                                    >
                                        Uložiť
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {addressImportPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-900">Import adries z Excelu</h2>
                                <button
                                    onClick={() => setAddressImportPopup(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                                >
                                    <IconX size={20} />
                                </button>
                            </div>
                            <div className="mb-4 p-4 bg-blue-50 rounded-md">
                                <h4 className="text-sm font-medium text-blue-800 mb-2">Formát súboru:</h4>
                                <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                                    <li>Excel súbor (.xlsx alebo .xls)</li>
                                    <li>Prvý riadok: Nazov | Typ | Ulica | Obec | PSC</li>
                                    <li>Každý ďalší riadok musí obsahovať všetkých päť údajov</li>
                                </ul>
                            </div>
                            <form onSubmit={onSubmitAddressImport} className="space-y-4">
                                <div>
                                    <label htmlFor="addressExcelFile" className="block text-sm font-medium text-gray-700 mb-1">
                                        Vyberte Excel súbor
                                    </label>
                                    <input
                                        id="addressExcelFile"
                                        name="addressExcelFile"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                        required
                                    />
                                </div>
                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setAddressImportPopup(false)}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                    >
                                        Zrušiť
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={addressImportLoading}
                                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {addressImportLoading ? "Importujem..." : "Importovať"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
export default Vseobecne